import { z } from 'zod';

const AlgoliaCollectionSchema = z.object({
  fields: z.object({
    slug: z.object({ 'en-US': z.string() }),
    description: z.object({ 'en-US': z.string().optional() }).optional(),
    collectionName: z.object({ 'en-US': z.string().optional() }).optional(),
    baseImage: z.object({ 'en-US': z.string().optional() }).optional(),
    roomType: z.object({ 'en-US': z.string().optional() }).optional().nullable(),
    collectionType: z.object({ 'en-US': z.string().optional().nullable() }).optional().nullable(),
    roomStyle: z.object({ 'en-US': z.string().optional() }).optional().nullable(),
    products: z
      .object({
        'en-US': z
          .array(
            z.object({
              sku: z.string().optional(),
              name: z.string().optional(),
              brand: z.string().optional(),
              image: z.string().optional(),
              price: z.number().optional(),
              exactMatch: z.boolean().optional(),
              category: z.object({ name: z.string().optional() }).optional(),
              surfaces: z.array(z.object({ label: z.string() })).optional(),
              referenceExactMatch: z.string().optional(),
            })
          )
          .optional(),
      })
      .optional(),
    designer: z
      .object({
        'en-US': z.object({
          sys: z.object({
            id: z.string(),
            type: z.string(),
          }),
        }),
      })
      .optional()
      .nullable(),
  }),
});

export const AlgoliaToCollectionSchema = AlgoliaCollectionSchema.transform((algoliaData) => {
  const fields = algoliaData?.fields || {};
  const products = (fields?.products?.['en-US'] || []).filter(
    (p): p is typeof p & { sku: string } => !!p.sku
  );

  return {
    slug: fields?.slug['en-US'],
    title: fields?.collectionName?.['en-US'] ?? '',
    description: fields?.description?.['en-US'] ?? '',
    image: fields?.baseImage?.['en-US'] ?? '',
    products,
    roomType: fields?.roomType?.['en-US'] ?? '',
    collectionType: fields?.collectionType?.['en-US'] ?? '',
    roomStyle: fields?.roomStyle?.['en-US'] ?? '',
    designerId: fields?.designer?.['en-US']?.sys?.id ?? '',
  };
});

const CollectionSchema = z.object({
  sys: z.object({
    id: z.string(),
  }),
  slug: z.string(),
  description: z.string().optional(),
  collectionName: z.string().optional(),
  baseImage: z.string().optional(),
  roomType: z.string().optional().nullable(),
  collectionType: z.string().optional().nullable(),
  roomStyle: z.string().optional().nullable(),
  products: z
    .array(
      z.object({
        sku: z.string().optional(),
        name: z.string().optional(),
        brand: z.string().optional(),
        image: z.string().optional(),
        price: z.number().optional(),
        exactMatch: z.boolean().optional(),
        category: z.object({ name: z.string().optional() }).optional(),
        surfaces: z.array(z.object({ label: z.string() })).optional(),
        referenceExactMatch: z.string().optional(),
      })
    )
    .optional(),
  designer: z
    .object({
      sys: z.object({
        id: z.string(),
      }),
    })
    .optional()
    .nullable(),
});

export const ApiToCollectionSchema = CollectionSchema.transform((apiData) => ({
  id: apiData.sys.id,
  slug: apiData.slug,
  title: apiData.collectionName ?? '',
  description: apiData.description ?? '',
  image: apiData.baseImage ?? '',
  roomType: apiData.roomType ?? '',
  collectionType: apiData.collectionType ?? '',
  roomStyle: apiData.roomStyle ?? '',
  products: (apiData.products ?? []).filter((p): p is typeof p & { sku: string } => !!p.sku),
  designerId: apiData.designer?.sys?.id ?? '',
}));

export type Collection = z.infer<typeof ApiToCollectionSchema>;
export type AlgoliaCollection = z.infer<typeof AlgoliaToCollectionSchema>;

export type AlgoliaCollectionWithIndex = AlgoliaCollection & {
  index?: number;
};
